/// <amd-module name="Wiki/Overview/Stores/WikiTreeStore" />
import { Store } from "VSS/Reflux/Store";
import { WikiPage } from "Wiki/Clients/Wiki/Contracts/Wiki";
import {
    FilteringCompletedPayload,
    PageDeletedPayload,
    PageHierarchyReloadedPayload,
    PageMovedPayload,
    PageReorderedPayload,
    PageSavedPayload,
    WikiPagesFetchedPayload
} from "Wiki/Overview/Actions/ActionCreatorPayloads";
import { isTopLevelPage } from "Wiki/Overview/Utils/WikiHelper";
import { WikiPageTreeItemProvider } from "Wiki/PageTree/WikiPageTreeItemProvider";
import { RepoConstants } from "Wiki/ViewCommon/Constants";

export interface PageHeirarchyFetchedPayload {
    parentPath: string;
    childPages: WikiPage[];
}

export interface PageToggledPayload {
    pagePath: string;
    isExpanded?: boolean;
}

export enum PagesHierarchyFetchedStatus {
    None,
    Partial,
    Complete
}

export interface WikiTreeState {
    treeItemProvider: WikiPageTreeItemProvider;
    homePagePath: string | undefined;
    isReorderInProgress: boolean;
    pagesFetchedStatus: PagesHierarchyFetchedStatus;
}

// should serve as an adapter over Path store
export class WikiTreeStore extends Store {
    private _state: WikiTreeState;

    constructor(isInitialTreeDataPopulated: boolean, initialPages: WikiPage[] | undefined, landingPagePath: string | undefined) {
        super("WikiTreeStore");

        const treeItemProvider = new WikiPageTreeItemProvider(initialPages || [], false);
        this._state = {
            treeItemProvider,
            pagesFetchedStatus: isInitialTreeDataPopulated ? PagesHierarchyFetchedStatus.Partial : PagesHierarchyFetchedStatus.None,
            isReorderInProgress: false,
            homePagePath: undefined // will set it in the next step
        };
        this._setHomePagePath();
    }

    public childPagesFetched = (childPagesFetchedPayload: PageHeirarchyFetchedPayload): void => {
        // currently we will not need this after all pages have been fetched once
        if (this._state.pagesFetchedStatus === PagesHierarchyFetchedStatus.Complete) {
            this._state.treeItemProvider.toggleItem(childPagesFetchedPayload.parentPath, true);
        } else {
            this._state.treeItemProvider.addChildItems(childPagesFetchedPayload.parentPath, childPagesFetchedPayload.childPages);
        }
        this.emitChanged();
    };

    public treeItemToggled = (payload: PageToggledPayload): void => {
        this._state.treeItemProvider.toggleItem(payload.pagePath, payload.isExpanded);
        this.emitChanged();
    };

    public onPageMoved = (payload: PageMovedPayload): void => {
        const oldPath = payload.sourcePage.path;
        const newPath = payload.pageMoveResponse.pageMove.newPath;
        const newOrder = payload.pageMoveResponse.pageMove.newOrder;
        if (isTopLevelPage(newPath) && newOrder === 0) {
            this._state.homePagePath = newPath;
            this.emitChanged();
        } else if (this._state.homePagePath === oldPath) {
            this._makeSecondPageAsHomePage();
            this.emitChanged();
        }
        this._state.treeItemProvider.moveItem(payload.sourcePage.path, newPath, newOrder);
        this.emitChanged();
    };

    public onPageHierarchyReloaded = (payload: PageHierarchyReloadedPayload): void => {
        this._state.treeItemProvider.onPageHierarchyReloaded(payload.pagePath, payload.flattenedHierarchy);
        this.emitChanged();
    };

    public onFilteringComplete = (payload: FilteringCompletedPayload): void => {
        payload.lastSelectedPage && this._state.treeItemProvider.prepareNextPageToSelect(payload.lastSelectedPage);
        this.emitChanged();
    };

    public onPageReorderStarted = (): void => {
        this._state.isReorderInProgress = true;
        this.emitChanged();
    };

    public loadPartialPages = (payload: WikiPagesFetchedPayload): void => {
        if (this._state.pagesFetchedStatus === PagesHierarchyFetchedStatus.None) {
            this._state.treeItemProvider.replaceAllItems(payload.pagesFetched);
            this._state.pagesFetchedStatus = PagesHierarchyFetchedStatus.Partial;
        }
        this._setHomePagePath();
        if (payload.pathToSelect) {
            this._state.treeItemProvider.prepareNextPageToSelect(payload.pathToSelect);
            this._state.treeItemProvider.toggleItem(payload.pathToSelect, true);
        }
        this.emitChanged();
    };

    public onError = (): void => {
        this._state.isReorderInProgress = false;
        this.emitChanged();
    };

    public onPageReordered = (payload: PageReorderedPayload): void => {
        if (isTopLevelPage(payload.pagePath)) {
            // we want to emit this change before updating treeitemprovider so that
            // re-render caused because of that updates icons - if required(set as home page)
            // if done afterwards, although tree component is rerendered, 'onrenderrow' of tree is not called
            // as nothing observable has changed
            // TODO: see if this solution will scale for any other use case
            if (payload.newOrder === 0) {
                this._state.homePagePath = payload.pagePath;
            } else {
                if (payload.oldOrder === 0) {
                    this._makeSecondPageAsHomePage();
                }
            }
        }

        this._state.isReorderInProgress = false;
        this._state.treeItemProvider.reorderItem(payload.pagePath, payload.newOrder);
        this.emitChanged();
    };

    public getState(): WikiTreeState {
        return this._state;
    }

    public loadAllPages = (payload: WikiPagesFetchedPayload): void => {
        if (this._state.pagesFetchedStatus === PagesHierarchyFetchedStatus.Partial) {
            this._state.treeItemProvider.completeExistingItems(payload.pagesFetched);
        } else {
            this._state.treeItemProvider.replaceAllItems(payload.pagesFetched);
        }
        this._setHomePagePath();
        payload.pathToSelect && this._state.treeItemProvider.prepareNextPageToSelect(payload.pathToSelect);
        this._state.pagesFetchedStatus = PagesHierarchyFetchedStatus.Complete;
        this.emitChanged();
    };

    public pageSaved = (payload: PageSavedPayload): void => {
        if (payload.oldPagePath === "") {
            // page added
            const newOrder = payload.page.order;
            if (isTopLevelPage(payload.page.path) && newOrder === 0) {
                this._state.homePagePath = payload.page.path;
            }

            this._state.treeItemProvider.addItem(payload.page);
            this.emitChanged();
        } else if (payload.oldPagePath != payload.page.path) {
            // rename
            if (this._state.homePagePath === payload.oldPagePath) {
                this._state.homePagePath = payload.page.path;
            }
            this._state.treeItemProvider.renameItem(payload.oldPagePath, payload.page);
            this.emitChanged();
        }
    };

    public pageDeleted = (payload: PageDeletedPayload): void => {
        // delete the page in tree
        this._state.treeItemProvider.deletePage(payload.deletedPagePath);

        // if current page was home page - remove
        if (this._state.homePagePath === payload.deletedPagePath) {
            const nextHomePage = this._state.treeItemProvider.getFirstConformantChild(RepoConstants.RootPath);
            this._state.homePagePath = nextHomePage ? nextHomePage.path : undefined;
        }
        this.emitChanged();
    };

    private _setHomePagePath(): void {
        const page = this._state.treeItemProvider.getNthChild(RepoConstants.RootPath, 1);
        if (page && !page.isNonConformant) {
            this._state.homePagePath = page.path;
        }
    }

    private _makeSecondPageAsHomePage(): void {
        const page = this._state.treeItemProvider.getNthChild(RepoConstants.RootPath, 2); // First is home page that is being modified
        if (page && !page.isNonConformant) {
            this._state.homePagePath = page.path;
        } else {
            // we don't have a homepage
            this._state.homePagePath = undefined;
        }
    }
}
